import React, { useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { getAssistantQuestions } from '../../api/assistant';
import AssistantPanel, { isFullScreen } from './AssistantPanel';
import useAssistantChat from './useAssistantChat';

const loadQuestions = ({ productType, productId }) =>
  getAssistantQuestions({ productType, productId });

/**
 * KD-3848 — "Ask about this hotel" on a TO factsheet: the floating assistant
 * (AssistantPanel), which a phone starts folded into its bar. Shows only when
 * the API answers (the organization is in the pilot). onSearchAvailability({
 * composition, startAt, endAt }) runs the page's booking search; a page that
 * cannot search leaves it out.
 */
export default function AssistantBlock({
  productType,
  productId,
  productName,
  context,
  locale,
  onSearchAvailability,
}) {
  const [view, setView] = useState(() => (isFullScreen() ? 'bar' : 'card'));
  const { data, error } = useAsync({
    promiseFn: loadQuestions,
    productType,
    productId,
    watch: productId,
  });
  const chat = useAssistantChat({ productType, productId, context, locale });
  // The trip this page was opened from, carried in the link: where "Ask the
  // DMC" sends an unanswered question. None when the trip is unsaved.
  const tripId =
    context === 'trip'
      ? (new URLSearchParams(window.location.search).get('tripId') ?? undefined)
      : undefined;

  // A new product is a new page session.
  const { reset } = chat;
  useEffect(() => reset(), [productId, reset]);

  if (error || !data) return null;

  const ask = question => {
    if (!question.trim() || chat.blocked) return;
    setView('chat');
    chat.ask(question.trim());
  };

  return (
    <AssistantPanel
      view={view}
      onViewChange={setView}
      productType={productType}
      productName={productName}
      questions={data.data?.questions ?? []}
      chat={chat}
      onAsk={ask}
      onSearchAvailability={onSearchAvailability}
      tripId={tripId}
    />
  );
}
